import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Status = 'starting' | 'live' | 'preview' | 'error';

/**
 * צילום חי במצלמה (לא רק העלאת קובץ) — משמש בכל מקום שבו ניתן לצרף תמונה
 * (יומן שדה, פענוח צופן). מציג תצוגה חיה מהמצלמה עם כפתור צילום, ואז תצוגה
 * מקדימה עם אישור/צילום חוזר לפני שהתמונה בפועל חוזרת לקורא (onCapture).
 */
export function CameraCapture({
  onCapture,
  onCancel,
}: {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<Status>('starting');
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('צילום ישיר דורש חיבור מאובטח (HTTPS) ודפדפן התומך במצלמה');
        setStatus('error');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStatus('live');
      } catch (e) {
        const name = e instanceof DOMException ? e.name : '';
        setError(
          name === 'NotAllowedError'
            ? 'הגישה למצלמה נדחתה — יש לאשר הרשאת מצלמה בהגדרות הדפדפן'
            : name === 'NotFoundError'
              ? 'לא נמצאה מצלמה במכשיר'
              : 'פתיחת המצלמה נכשלה',
        );
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.videoWidth === 0) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setStatus('preview');
      },
      'image/jpeg',
      0.85,
    );
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setStatus('live');
  };

  const confirm = () => {
    if (!previewBlob) return;
    stopStream();
    onCapture(previewBlob);
  };

  const cancel = () => {
    stopStream();
    onCancel();
  };

  return createPortal(
    <div className="camera-capture" role="dialog" aria-modal="true" aria-label="צילום תמונה">
      <div className="camera-capture__frame">
        {status === 'error' && (
          <div className="camera-capture__error">
            <p>⚠️ {error}</p>
            <button type="button" className="btn btn--sm" onClick={cancel}>
              סגור
            </button>
          </div>
        )}

        {status !== 'error' && status !== 'preview' && (
          <video ref={videoRef} className="camera-capture__video" playsInline muted />
        )}
        {status === 'starting' && <div className="camera-capture__hint">פותח מצלמה...</div>}

        {status === 'preview' && previewUrl && (
          <img src={previewUrl} alt="תצוגה מקדימה" className="camera-capture__video" />
        )}

        <canvas ref={canvasRef} className="camera-capture__canvas" />
      </div>

      <div className="camera-capture__controls">
        {status === 'live' && (
          <>
            <button type="button" className="btn btn--ghost" onClick={cancel}>
              ביטול
            </button>
            <button type="button" className="camera-capture__shutter" onClick={capture} aria-label="צלם" />
          </>
        )}
        {status === 'preview' && (
          <>
            <button type="button" className="btn btn--ghost" onClick={retake}>
              ↺ צלם שוב
            </button>
            <button type="button" className="btn btn--primary" onClick={confirm}>
              ✓ השתמש בתמונה
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
